import React from 'react';
import { Autocomplete, Checkbox, TextField, Chip, Tooltip, ListSubheader, Box } from '@mui/material';
import { maxHeight, styled } from '@mui/system';
import Popper from '@mui/material/Popper';
import { useTranslation } from 'react-i18next';
import FixedSizeVirtualizedListbox from '../AutocompleteCardSelect/FixedSizeVirtualizedListbox';

const VIRTUALIZATION_OPTIONS_THRESHOLD = 50;
const LIST_ITEM_HEIGHT = 40;
const LIST_HEIGHT = 320;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--Spacing-md, 8px)',
    background: 'var(--bg-input, #F5F5F6)',
    '& .MuiAutocomplete-tag': {
      maxHeight: '32px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
    background: 'var(--bg-input, #F5F5F6)',
    borderRadius: 'var(--Spacing-md, 8px)',
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
    flexWrap: 'nowrap',
  },
  '& .MuiInputBase-root': {
    boxSizing: 'border-box !important',
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: 'var(--autocomplete-popper-z, 1500) !important',
  '& .MuiPaper-root': {
    borderRadius: 'var(--Spacing-md, 8px)',
    background: 'var(--white, #FFF)',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
    minWidth: '350px !important',
    maxWidth: '650px !important',
    maxHeight: 'min(400px, calc(100vh - 120px)) !important',
    overflow: 'auto',
  },
  '& .MuiAutocomplete-listbox': {
    padding: theme.spacing(1),
    maxHeight: 'none',
  },
  '& .MuiListSubheader-root': {
    width: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-option': {
    borderRadius: 'var(--Spacing-sm, 4px)',
    '&[aria-selected="true"], &.Mui-focused, &:hover': {
      background: 'var(--orange, #F9AA2A) !important',
    },
  },
}));

// When used inside MUI DataGrid, the grid's native keydown listener runs before React's.
// This wrapper stops Arrow/Enter/Escape in the native bubble phase so the grid never receives them.
const GRID_NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
function DataGridKeyCaptureWrapper({ children }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e) => {
      if (GRID_NAV_KEYS.includes(e.key) && el.contains(e.target)) e.stopPropagation();
    };
    el.addEventListener('keydown', handler, false);
    return () => el.removeEventListener('keydown', handler, false);
  }, []);
  return <div ref={ref} style={{ width: '100%', display: 'inline-block' }}>{children}</div>;
}

const AutocompleteSelect = ({
  options,
  selectedValues,
  onChange,
  label = '',
  placeholder = 'Type to search',
  isMultiple = true,
  width = '165px', // Default width
  isDisabled = false,
  onRightClick = null,
  hasError = false,
  inputHeight = null,
  direction = 'rtl', // Add direction prop
  textAlign = null, // Add textAlign prop
  inputProps = {}, // Add inputProps prop
  groupBy = null,
  renderGroup = null,
  hideCheckboxes = false, // Hide the per-option checkbox; click the row to toggle selection
  popperZIndex = null,
  disablePortal = false,
}) => {
  const { i18n, t } = useTranslation();
  const isRtl = direction === 'rtl' || ['he', 'ar'].includes(i18n.language);
  const [inputValue, setInputValue] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(false);
  const [listboxOpen, setListboxOpen] = React.useState(false);
  const prevSelectedValuesRef = React.useRef(selectedValues);

  const scrollSelectedOptionIntoView = React.useCallback(() => {
    const poppers = document.querySelectorAll('.MuiAutocomplete-popper');
    const popper = poppers[poppers.length - 1];
    const selectedOption = popper?.querySelector('.MuiAutocomplete-option[aria-selected="true"]');
    if (selectedOption) {
      selectedOption.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, []);

  // Reset isTyping when selection changes externally (not from user interaction)
  React.useEffect(() => {
    // When selectedValues changes and we're not actively typing, ensure we show the selected value
    const hasSelection = (!isMultiple && selectedValues) || (isMultiple && selectedValues?.length > 0);
    if (hasSelection) {
      // Small delay to ensure this runs after any user interaction
      const timer = setTimeout(() => {
        if (!isTyping) {
          setInputValue('');
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [selectedValues, isMultiple]);

  // Input style for RTL support
  const inputPropsStyle = {
    disableUnderline: true,
    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
    sx: {
      height: '48px',
      padding: '0 10px',
      borderRadius: '8px',
      background: '#304FFF',
      border: '1px solid transparent',

    },
  };

  const inputStyle = {
    style: { 
      textAlign: textAlign || (direction === 'ltr' ? 'left' : 'right'),
      ...inputProps?.style 
    },
    ...inputProps,
  };

  const isAllSelected = isMultiple
    ? options.length > 0 && selectedValues.length === options.length
    : false;

  // Custom filter function for better search - memoized for performance.
  // When the user is not actively typing, return all options so a selected
  // label (e.g. "זמין") does not hide the rest of the list (e.g. בקומיסיון).
  const filterOptions = React.useCallback((options, { inputValue }) => {
    if (!isTyping || !inputValue || inputValue.trim() === '') {
      return options;
    }
    const searchTerm = inputValue.toLowerCase().trim();
    return options.filter((option) => {
      if (option.value === 'select_all') return true;
      const label = (option.label || '').toLowerCase();
      const name = (option.name || '').toLowerCase();
      const accountCaseId = (option.accountCaseId || '').toLowerCase();
      const cardIdStr = String(option.cardId ?? option.value ?? '').toLowerCase();
      const extId = (option.externalSoftwareCardNumber || '').trim().toLowerCase();
      const matchExternal = extId !== '' && extId.includes(searchTerm);
      const makat = String(option.makat ?? '').toLowerCase();
      return label.includes(searchTerm) || name.includes(searchTerm) ||
             accountCaseId.includes(searchTerm) || cardIdStr.includes(searchTerm) || matchExternal ||
             (makat !== '' && makat.includes(searchTerm));
    });
  }, [isTyping]);

  // Handle Enter: when listbox is open, let MUI handle it (arrow-highlighted option). When closed, optionally select first match.
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && listboxOpen) {
      // Listbox is open: let MUI select the highlighted option (don't prevent default)
      return;
    }
    if (event.key === 'Enter' && !isMultiple && inputValue.trim()) {
      event.preventDefault();
      event.stopPropagation();
      const filteredOptions = filterOptions(allOptions, { inputValue });
      const validOptions = filteredOptions.filter(opt => opt.value !== 'select_all');
      if (validOptions.length > 0) {
        handleOnChange(event, validOptions[0]);
        setInputValue('');
      }
    }
  };

  const handleOnChange = (event, newValue) => {
    if (isMultiple) {
      if (newValue.some((option) => option.value === 'select_all')) {
        if (isAllSelected) {
          onChange(event, []);
        } else {
          onChange(event, options);
        }
      } else {
        const filteredValues = newValue.filter(
          (option) => option.value !== 'select_all'
        );
        onChange(event, filteredValues);
      }
    } else {
      // For single select, clear inputValue immediately when selection is made
      // This ensures the selected value's label is displayed
      if (newValue) {
        setInputValue('');
        setIsTyping(false);
      }
      onChange(event, newValue);
    }
  };

  const renderTags = (value, getTagProps) => {
    if (value.length === 0) {
      return null;
    }

    const displayedValues = value.slice(0, 1);
    const remainingCount = value.length - displayedValues.length;

    return (
      <>
        {displayedValues.map((option, index) => (
          <Chip
            key={option.value}
            label={option.label}
            {...getTagProps({ index })}
          />
        ))}
        {remainingCount > 0 && (
          <Chip label={`+${remainingCount} ${t('More')}`} />
        )}
      </>
    );
  };

  const allOptions = React.useMemo(() => {
    if (isMultiple) {
      return [{ label: t('Select all'), value: 'select_all' }, ...options];
    } else {
      return options;
    }
  }, [options, t, isMultiple]);

  const hasValue = isMultiple
    ? selectedValues.length > 0
    : selectedValues !== null && selectedValues !== undefined;

  const inputPlaceholder = hasValue ? '' : placeholder;

  const defaultRenderGroup = (params) => (
    <li key={params.key} dir={isRtl ? 'rtl' : 'ltr'}>
      <ListSubheader
        component="div"
        disableSticky
        sx={{
          backgroundColor: '#eef2ff',
          color: '#304FFF',
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '32px',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: isRtl ? 'flex-end' : 'flex-start',
          alignItems: 'center',
          textAlign: isRtl ? 'right' : 'left',
          direction: isRtl ? 'rtl' : 'ltr',
          padding: isRtl ? '4px 16px 4px 8px' : '4px 8px 4px 16px',
        }}
      >
        {params.group}
      </ListSubheader>
      <ul
        style={{
          padding: 0,
          margin: 0,
          direction: isRtl ? 'rtl' : 'ltr',
          textAlign: isRtl ? 'right' : 'left',
        }}
      >
        {params.children}
      </ul>
    </li>
  );

  const renderOption = (props, option, state) => {
    const { key, ...optionProps } = props;
    const { selected } = state;
    let isChecked = selected;
    if (isMultiple && option.value === 'select_all') {
      isChecked = isAllSelected;
    }

    return (
      <li
        key={key}
        {...optionProps}
        onContextMenu={(e) => {
          e.preventDefault();
          if (onRightClick) {
            onRightClick(option);
          }
        }}
        style={{
          ...optionProps.style,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          direction: isRtl ? 'rtl' : 'ltr',
          textAlign: isRtl ? 'right' : 'left',
          width: '100%',
          justifyContent: 'flex-start',
        }}
      >
        {isMultiple && !hideCheckboxes && (
          <Checkbox
            checked={isChecked}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={{ p: 0.5, flexShrink: 0, color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
          />
        )}
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: isRtl ? 'right' : 'left',
          }}
        >
          {option.label}
        </Box>
      </li>
    );
  };

  const renderInput = (params) => {
    const MAX_PLACEHOLDER_LENGTH = 50;
    const isPlaceholderLong = placeholder.length > MAX_PLACEHOLDER_LENGTH;

    const hasValue = isMultiple
      ? selectedValues.length > 0
      : selectedValues !== null && selectedValues !== undefined;

    const inputPlaceholder = hasValue ? '' : placeholder;

    const textField = (
      <TextField
        {...params}
        variant="outlined"
        label={label}
        placeholder={inputPlaceholder}
        onKeyDown={(e) => {
          if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) e.stopPropagation();
          params.inputProps?.onKeyDown?.(e);
          handleKeyDown(e);
        }}
        onFocus={(e) => {
          // When user focuses on input with a selection, prepare for potential typing
          // This ensures filtering works immediately when they start typing
          if (!isMultiple && selectedValues && !isTyping) {
            // User clicked on input - they might want to search/change
            // Keep inputValue empty so selected value shows, but be ready for typing
            // The onInputChange will handle it when they actually type
          }
          // Call the original onFocus if it exists
          if (params.inputProps?.onFocus) {
            params.inputProps.onFocus(e);
          }
        }}
        InputProps={{
          ...params.InputProps,
          ...inputPropsStyle,
        }}
        inputProps={{
          ...params.inputProps,
          ...inputStyle,
        }}
      />
    );

    return isPlaceholderLong ? (
      <Tooltip title={placeholder}>{textField}</Tooltip>
    ) : (
      textField
    );
  };

  return (
    <DataGridKeyCaptureWrapper>
    <StyledAutocomplete
      multiple={isMultiple}
      options={allOptions}
      value={selectedValues}
      onChange={handleOnChange}
      inputValue={isTyping ? inputValue : undefined}
      onInputChange={(event, newInputValue, reason) => {
        // Handle input changes for both filtering and selection display
        if (reason === 'input') {
          // User is typing - take control of inputValue immediately for real-time filtering
          // Set both isTyping and inputValue in the same update cycle
          setIsTyping(true);
          setInputValue(newInputValue);
        } else if (reason === 'reset') {
          // Selection was made - stop controlling inputValue so MUI shows selected value
          setIsTyping(false);
          setInputValue('');
        } else if (reason === 'clear') {
          // User cleared the input
          setIsTyping(false);
          setInputValue('');
        } else if (reason === 'blur' && !isMultiple) {
          // On blur, if there's a selection, stop controlling inputValue
          if (selectedValues) {
            setIsTyping(false);
            setInputValue('');
          }
        }
      }}
      filterOptions={filterOptions}
      disableListWrap={false}
      {...(groupBy && {
        groupBy,
        renderGroup: renderGroup || defaultRenderGroup,
      })}
      {...(options.length > VIRTUALIZATION_OPTIONS_THRESHOLD && !groupBy && {
        ListboxComponent: FixedSizeVirtualizedListbox,
      })}
      getOptionLabel={(option) => {
        if (!option) return '';
        return option.label || '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (!value || !option) return false;
        // Handle both object and primitive value comparisons
        const optionValue = typeof option === 'object' ? option.value : option;
        const valueValue = typeof value === 'object' ? value.value : value;
        return optionValue === valueValue;
      }}
      PopperComponent={StyledPopper}
      ListboxProps={{
        dir: isRtl ? 'rtl' : 'ltr',
        style: {
          direction: isRtl ? 'rtl' : 'ltr',
          textAlign: isRtl ? 'right' : 'left',
        },
      }}
      PopperProps={{
        disablePortal,
        style: {
          ...(popperZIndex != null
            ? { zIndex: popperZIndex, ['--autocomplete-popper-z']: String(popperZIndex) }
            : {}),
        },
        modifiers: [
          { name: 'flip', enabled: true, options: { fallbackPlacements: ['top', 'bottom'] } },
          { name: 'preventOverflow', enabled: true, options: { padding: 8, rootBoundary: 'viewport' } },
        ],
        sx: {
          zIndex: `${popperZIndex ?? 1500} !important`,
          '& .MuiPaper-root': {
            width: width,
          },
        },
      }}
      componentsProps={{
        popper: {
          style: {
            zIndex: popperZIndex ?? 1500,
            ...(popperZIndex != null ? { ['--autocomplete-popper-z']: String(popperZIndex) } : {}),
          },
          sx: { zIndex: `${popperZIndex ?? 1500} !important` },
        },
      }}
      limitTags={1}
      renderTags={renderTags}
      renderOption={renderOption}
      renderInput={renderInput}
      openOnFocus={true}
      clearOnBlur={false}
      selectOnFocus={false}
      autoHighlight={true}
      autoComplete={false}
      onOpen={() => {
        setListboxOpen(true);
        if (!isTyping && ((!isMultiple && selectedValues) || (isMultiple && selectedValues?.length > 0))) {
          setIsTyping(false);
          setInputValue('');
        }
        // Wait for the listbox to mount, then scroll the selected option into view
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollSelectedOptionIntoView);
        });
      }}
      onClose={() => setListboxOpen(false)}
      sx={{
        width,
        minWidth: width,

        /* keep the custom height logic */
        ...(inputHeight && {
          height: inputHeight,
          '& .MuiOutlinedInput-root': {
            height: inputHeight,
            boxSizing: 'border-box',
          },
          '& .MuiAutocomplete-inputRoot': {
            height: inputHeight,
          },
        }),

        /* ② red outline + subtle pulse when hasError === true */
        ...(hasError && {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#f44336 !important',
            borderWidth: 2,
          },
          animation: 'pulse 0.6s ease-in-out 0s 2',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.03)' },
            '100%': { transform: 'scale(1)' },
          },
        }),
      }}
      disabled={isDisabled}
    />
    </DataGridKeyCaptureWrapper>
  );
};

export default AutocompleteSelect;